import { html, css, LitElement } from 'lit';
import { repeat } from 'lit/directives/repeat.js';
import { Image } from '../Image/Image';
import { Text } from '../Text/Text';
import '../Inputbox/Input';
import '../ChatTile/ChatTile';
import '../ContactInfo/ContactInfo';
import '../../Components/Loader/Spinner/Spinner';
import { debounce, generateProfileImageByInitials } from '../../../lib/helpers';
import { getImageSrc, getProductImage } from '../../../utils/utils';
import { formatUnixTimestamp } from '../../../utils/unixTimeConverter';
import { drawerDialogService } from '../Dialog-Drawer/dialog-drawer';
import { fetchReasons, cancelDealOrder, getDealQRCode, generateQrCode, validateQr, editOffer, counterOffer, acceptOffer, submitRating, cancelOffer } from '../../../api/head-apis';
import { handleApiError, handleApiSuccess } from '../../../utils/error-handler.js';
import '../Dialog-Drawer/cancel-deal-dialog.js';
import '../Dialog-Drawer/cancel-offer-dialog.js';
import { showToast } from '../Toaster/toast-helper';

class Head extends LitElement {
  static properties = {
    //objects
    image: { type: Object },
    text: { type: Object },
    isTyping: { type: Boolean },
    typingUserName: { type: String },
    confirmationAction: { type: String },
    isBlocked: { type: Boolean | String },
    onPaymentClicked: {type: Function},
    //arrays
    conversationDetails: {
      type: Array,
      hasChanged(newVal, oldVal) {
        if (newVal?.metaData?.offerId !== oldVal?.metaData?.offerId) {
          this.handleCancelButton();
          this.clearAndRecalculateStates();
        } 
      
        return true;
      },
    },
    userMetaData: { type: Object },

    //functions
    onFirstSvgClick: { type: Function },
    onClearChat: { type: Function },
    onSearchInput: { type: Function },
    onCloseChat: { type: Function },
    onTextClick: { type: Function },
    onChatTileSelected: { type: Function },


    //booleans
    isPopupOpen: { type: Boolean },
    isActionsNeeded: { type: Boolean },
    messageAction: { type: Object },
    isLoadingMessage: { type: Boolean },
    isQrCodeLoading: { type: Boolean },
    isAcceptOfferLoading: { type: Boolean },
    isEditOfferLoading: { type: Boolean },
    isCounterOfferLoading: { type: Boolean },
    isCancelOfferLoading: { type: Boolean },
    showFullScreenLoader: { type: Boolean },
    isCancellingDeal: { type: Boolean },

    //strings
    lastSeenText: { type: String },
    lastSeenTextColor: { type: String },
    lastSeenTextFontSize: { type: String },
    padding: { type: String },
    profileImage: { type: String },
    productImage: { type: Object },
    backgroundColor: { type: String },
    textColor: { type: String },
    svgFillColor: { type: String },
    boxShadow: { type: String },
    borderRadius: { type: String },
    borderColor: { type: String },
    borderStyle: { type: String },
    borderWidth: { type: String },
    leofferAccountId: { type: String },
    showConfirmationPopup: { type: Boolean },
    isGroupcast: { type: Boolean },
    onBackButtonClick: {type: Function},
    userToken: { type: String },
    theme: { type: String }, // 'light' or 'dark'
    
    // Internal state properties for renderActionButtons reactivity
    isBuyer: { type: Boolean },
    isOffer: { type: Boolean },
    isBuyNow: { type: Boolean },
  };

  constructor() {
    super();
    this.isPopupOpen = false;
    this.showConfirmationPopup = false;
    this.searchValue = '';
    this.confirmationAction = '';
    this.isBuyer = false;
    this.isOffer = false;
    this.isBuyNow = false;
    this.isQrCodeLoading = false;
    this.isAcceptOfferLoading = false;
    this.isEditOfferLoading = false;
    this.isCounterOfferLoading = false;
    this.isCancelOfferLoading = false;
    this.showFullScreenLoader = false;
    this.isCancellingDeal = false;
    this.handleDocumentClick = this.handleDocumentClick.bind(this);
    this.debouncedSearchHandler = debounce(query => {
      this.onSearchInput(query);
    }, 500);
    const urlParams = new URLSearchParams(window.location.search);
    this.isGroupcast = urlParams.has('groupcast');
    // New state for reasons popup
    this.isReasonsPopupOpen = false;
    this.reasonsList = [];
    this.selectedReasonId = '';
    this.otherReasonText = '';
    this.isOtherSelected = false;
    this.isLoadingReasons = false;
    this.reasonsError = '';
    // Set default theme to light
    this.theme = 'light';
    
    // Search related properties
    this.searchedMessageDetails = [];
    this.searchText = '';
    this.isSearchOpen = false;
    this.isConsentPopupOpen = false;
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener('click', this.handleDocumentClick);
    
    // Listen for chat tile selection events
    document.addEventListener('chatTileSelected', (event) => {
      const conversationDetails = event.detail;
      this.handleChatTileSelection(conversationDetails);
    });
    
    // Listen for offer cancelled events
    document.addEventListener('offer-cancelled', (event) => {
      // Clear loading states and update UI
      this.isCancelOfferLoading = false;
      this.requestUpdate();
    });
    
    // Listen for deal cancelled events
    document.addEventListener('deal-cancelled', (event) => {
      // Reset the cancelling flag
      this.isCancellingDeal = false;
      this.requestUpdate();
    });
    
 
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('click', this.handleDocumentClick);
    
    // Remove chat tile selection event listener
    document.removeEventListener('chatTileSelected', (event) => {
      const conversationDetails = event.detail;
      this.handleChatTileSelection(conversationDetails);
    });
    
    // Remove cancel event listeners
    document.removeEventListener('offer-cancelled', (event) => {
      this.isCancelOfferLoading = false;
      this.requestUpdate();
    });
    
    document.removeEventListener('deal-cancelled', (event) => {
      this.requestUpdate();
    });
  }

  handleDocumentClick(event) {
    const target = event.composedPath()[0];
    const contactInfoElement = this.shadowRoot.querySelector('contact-info');

    const isInsideContactInfo = event.composedPath().some(element => element === contactInfoElement || element === contactInfoElement?.shadowRoot);

    if (!target.closest('.ctas-popup') && !target.closest('.kebab-menu')) {
      this.isPopupOpen = false;
    }
  }

  static styles = [
    css`
      :host {
        --header-bg: var(--bg-primary-color, #ffffff);
        --header-border: var(--border-color, #e1e5e9);
        --text-primary: var(--text-color, #1a1a1a);
        --text-secondary: #6b7280;
        --text-accent: #241172;
        --shadow-light: 0 1px 3px rgba(0, 0, 0, 0.1);
        --shadow-medium: 0 4px 6px rgba(0, 0, 0, 0.1);
        --shadow-heavy: 0 10px 25px rgba(0, 0, 0, 0.15);
        --border-radius: 12px;
        --transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
        --popup-bg: #18181b;
        --popup-text: #fff;
        --popup-option-bg: #23232a;
        --popup-option-bg-selected: #241172;
        --popup-option-text: #fff;
        --popup-option-text-selected: #fff;
        --popup-btn-bg: #241172;
        --popup-btn-text: #fff;
        --popup-btn-radius: 8px;
        --popup-btn-font: 16px;
        --popup-overlay-bg: rgba(0,0,0,0.7);
        --popup-error: #ff6b6b;
      }
      :host([theme='light']),
      @media (prefers-color-scheme: light) {
        --popup-bg: #fff;
        --popup-text: #18181b;
        --popup-option-bg: #f3f4f6;
        --popup-option-bg-selected: #241172;
        --popup-option-text: #18181b;
        --popup-option-text-selected: #fff;
        --popup-btn-bg: #241172;
        --popup-btn-text: #fff;
        --popup-overlay-bg: rgba(0,0,0,0.25);
        --popup-error: #d32f2f;
      }
      .edit-offer-button,
      .counter-offer-button,
      .exchange-offer-accept-button {
        background-color: #241172 !important;
        color: #fff;
      }

      .action-button:disabled {
        opacity: 0.6;
        cursor: not-allowed;
        pointer-events: none;
      }

      .full-screen-loader {
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: rgba(0, 0, 0, 0.8);
        display: flex;
        justify-content: center;
        align-items: center;
        z-index: 999999;
        backdrop-filter: blur(5px);
      }

      .loader-content {
        background: var(--bg-primary-color);
        border-radius: 12px;
        padding: 32px;
        text-align: center;
        box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
      }

      .loader-spinner {
        width: 40px;
        height: 40px;
        border: 4px solid var(--bg-secondary-color);
        border-top: 4px solid var(--base-color);
        border-radius: 50%;
        animation: spin 1s linear infinite;
        margin: 0 auto 16px;
      }

      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }

      .loader-text {
        color: var(--text-primary-color);
        font-size: 16px;
        font-weight: 500;
      }

      .action-button:disabled:hover {
        transform: none;
        box-shadow: none;
      }
    .offer-accepted-tag{
      background-color: #1CA48F1A;
      color: #1CA48F;
      padding: 4px 8px;
      border-radius: 4px;
      font-size: 12px;
    }
      contact-info {
        position: absolute;
        right: 0;
        width: 25%;
        min-width: var(--sidebar-width);
        height: fit-content;
        top: 0;
        z-index: 10000;
      }

      .head-container {
        display: flex;
        align-items: start;
        position: relative;
        flex-direction: column;
        gap: 8px;
        background: var(--header-bg);
        border-bottom: 1px solid var(--header-border);
        padding: 16px 20px;
        box-shadow: var(--shadow-light);
      }

      .head-main-content {
        display: flex;
        align-items: start;
        width: 100%;
        flex-direction: column;
        gap: 12px;
      }

      .head-opponent-container div {
        display: flex;
        align-items: center;
        width: 100%;
        gap: 12px;
      }

      .head-opponent-container div span {
        display: flex;
        align-items: flex-start;
        width: 100%;
        flex-direction: column;
        gap: 4px;
      }

   

      .last-seen {
        font-size: 12px;
        color: var(--text-accent);
        font-weight: 500;
      }

      .actions-container {
        display: flex;
        align-items: center;
        justify-content: space-between;
        width: 50%;
      }

      .head-product-container {
        display: flex;
        align-items: center;
        justify-content: space-between;
        width: 100%;
        padding: 0;
      }

      .head-product-details-container {
        display: flex;
        align-items: center;
        width: 50%;
        gap: 16px;
      }

      .product-price-container {
        display: flex;
        flex-direction: column;
        gap: 4px;
        align-items: start;
      }

      .image-container {
        cursor: pointer;
        transition: var(--transition);
        border-radius: 8px;
      }

      .text-container {
        cursor: pointer;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        gap: 4px;
        transition: var(--transition);
      }

      .text-container:hover {
        opacity: 0.8;
      }

      .actions {
        display: flex;
        gap: 8px;
        margin-left: auto;
        cursor: pointer;
      }

      .kebab-menu {
        transition: var(--transition);
        border-radius: 50%;
        padding: 8px;
      }

      .kebab-menu:hover {
        background-color: var(--bg-quinary-color, #f3f4f6);
        transform: scale(1.1);
      }

      .ctas-popup {
        position: absolute;
        top: 70px;
        right: 20px;
        padding: 8px;
        background-color: var(--header-bg);
        border: 1px solid var(--header-border);
        box-shadow: var(--shadow-heavy);
        border-radius: var(--border-radius);
        z-index: 10;
        display: flex;
        flex-direction: column;
        gap: 2px;
        min-width: 200px;
        backdrop-filter: blur(10px);
      }

      .ctas-popup button {
        cursor: pointer;
        border: none;
        font-size: 14px;
        background: transparent;
        color: var(--text-primary) !important;
        border-bottom: 1px solid var(--header-border) !important;
        padding: 12px 16px;
        display: flex;
        gap: 8px;
        align-items: center;
        transition: var(--transition);
        border-radius: 6px;
        margin: 2px;
      }

      .ctas-popup button:hover {
        background-color: var(--bg-quinary-color, #f3f4f6);
      }

      .ctas-popup button:last-child {
        border: none !important;
      }

      .consent-popup-container {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        width: 100%;
        height: 100%;
        background: rgba(0, 0, 0, 0.6);
        display: flex;
        justify-content: center;
        align-items: center;
        z-index: 99;
        backdrop-filter: blur(4px);
      }

      .searchMessageContainer {
        position: fixed;
        top: 0;
        right: 0;
        width: 350px;
        height: 100vh;
        background: var(--header-bg);
        z-index: 100;
        padding: 24px;
        transform: translateX(100%);
        transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        box-shadow: var(--shadow-heavy);
        border-left: 1px solid var(--header-border);
      }

      .searchMessageContainer.open {
        transform: translateX(0);
      }

      .search-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 24px;
        font-size: 20px;
        padding-bottom: 16px;
        border-bottom: 1px solid var(--header-border);
      }

      .search-header h3 {
        color: var(--text-primary);
        margin: 0;
        font-weight: 600;
      }

      .close-search {
        background: none;
        border: none;
        color: var(--text-primary);
        cursor: pointer;
        font-size: 20px;
        padding: 8px;
        border-radius: 50%;
        transition: var(--transition);
      }

      .close-search:hover {
        background-color: var(--bg-quinary-color, #f3f4f6);
      }

      .search-input-container {
        position: relative;
        margin-bottom: 20px;
      }

      .search-input-container my-input::part(input) {
        border: 1px solid var(--header-border);
        outline: none;
        transition: var(--transition);
        border-radius: 8px;
      }

      .search-input-container my-input::part(input):focus {
        border: 1px solid var(--text-accent);
        box-shadow: 0 0 0 3px rgba(109, 62, 193, 0.1);
      }

      .search-results {
        margin-top: 20px;
        height: calc(100vh - 180px);
        overflow-y: auto;
      }

      ::-webkit-scrollbar {
        width: 6px;
      }

      ::-webkit-scrollbar-track {
        background: transparent;
      }

      ::-webkit-scrollbar-thumb {
        background-color: var(--bg-tertiary-color, #d1d5db) !important;
        border-radius: 10px;
      }

      .consent-popup {
        background: var(--header-bg);
        position: relative;
        padding: 32px;
        border-radius: var(--border-radius);
        width: 400px;
        box-shadow: var(--shadow-heavy);
        border: 1px solid var(--header-border);
      }

      .consent-popup h3 {
        margin: 0 0 16px 0;
        color: var(--text-primary);
        font-size: 20px;
        font-weight: 600;
      }

      .consent-popup p {
        margin: 0 0 16px 0;
        color: var(--text-secondary);
        line-height: 1.6;
      }

      .consent-popup button {
        background: var(--header-bg);
        border: 1px solid var(--base-color, #6b7280);
        color: var(--base-color, #6b7280);
        padding: 12px 24px;
        font-weight: 600;
        cursor: pointer;
        border-radius: 6px;
        transition: var(--transition);
        margin-right: 12px;
      }

      .consent-popup button:hover {
        opacity: 0.8;
      }

      .consent-popup button:nth-child(2) {
        background: var(--base-color, #6b7280);
        border: 1px solid var(--header-bg);
        color: var(--header-bg);
      }

      .loading-container-top {
        width: 100%;
        position: absolute;
        top: 50%;
        left: 0;
        background-color: var(--bg-primary-color);
        padding: 10px;
      }
      .popup-overlay {
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background-color: rgba(0, 0, 0, 0.6);
        display: flex;
        justify-content: center;
        align-items: center;
        z-index: 1000;
      }
      .popup-content {
        background: var(--bg-primary-color);
        padding: 14px;
        border-radius: 8px;
        width: 28%;
        max-height: 80vh;
        box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
      }
      .close-btn {
        background: none;
        border: none;
        font-size: 24px;
        cursor: pointer;
      }
      .popup-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 0px 16px;
      }
      .popup-body {
        padding: 0 16px;
      }

      .popup-body-buttons {
        display: flex;
        justify-content: space-between;
        gap: 10px;
      }

      .popup-body button {
        border: none;
        font-size: 16px;
        color: var(--text-primary-color);
        cursor: pointer;
        transition: var(--transition);
        border-radius: 8px;
        margin-bottom: 8px;
      }

      .search-results-container:hover {
        background-color: var(--bg-quinary-color, #f3f4f6);
        width: 50%;
        padding: 10px 0px;
      }

      .popup-body button:last-child {
        background: transparent;
        border: 1px solid var(--text-primary-color);
        border-radius: 20px;
      }
      .popup-body button:first-child {
        background: var(--text-primary-color);
        border-radius: 20px;
      }
      .iconButton {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 6px;
      }
      .shimmer-loader {
        width: 90%;
        height: 45px;
        position: relative;
        overflow: hidden;
      }

      .shimmer-line {
        position: absolute;
        top: 0;
        left: -100%;
        width: 100%;
        height: 100%;
        background: linear-gradient(90deg, var(--bg-secondary-color) 25%, var(--bg-primary-light) 50%, var(--bg-secondary-color) 75%);
        animation: shimmer 1.5s infinite;
      }
      @keyframes shimmer {
        0% {
          left: -100%;
        }
        100% {
          left: 100%;
        }
      }

.back-button {
  display: none;
}
      @media (max-width: 768px) {
        .consent-popup {
          width: 90%;
          margin: 20px;
        }
          

        .back-button {
          display: block;
          cursor: pointer;
          margin-right: 5px;
        }
        contact-info {
          width: 100%;
        }

        .head-container {
          padding: 12px 16px;
        }

        .head-product-details-container {
          width: 60%;
        }

        .actions-container {
          width: 40%;
        }
      }
      .product-price-container {
        display: flex;
        flex-direction: column;
        gap: 2px;
        align-items: flex-start;
        margin-top: 2px;
      }
      .action-button {
        background: #000;
        color: #fff;
        border: none;
        border-radius: 6px;
        padding: 8px 18px;
        font-weight: 600;
        font-size: 14px;
        cursor: pointer;
        transition: background 0.2s;
        margin-left: 10px;
        box-shadow: 0 1px 3px rgba(0,0,0,0.07);
      }
      .action-button:hover {
        background: #222;
      }
      .deal-cancelled-tag {
        background: #dc3545;
        color: white;
        padding: 8px 16px;
        border-radius: 20px;
        font-size: 14px;
        font-weight: 600;
        display: inline-block;
        text-align: center;
      }

        .review-submitted-tag {
        background: #1CA48F;
        color: white;
        padding: 8px 16px;
        border-radius: 20px;
        font-size: 14px;
        font-weight: 600;
        display: inline-block;
        text-align: center;
        }
      .rate-button {
        background: #241172;
        color: white;
        border: none;
        border-radius: 6px;
        padding: 8px 18px;
        font-weight: 600;
        font-size: 14px;
        cursor: pointer;
        transition: background 0.2s;
        box-shadow: 0 1px 3px rgba(0,0,0,0.07);
      }
    
      .icon-button {
        background: #f3f4f6;
        border: none;
        border-radius: 50%;
        padding: 7px;
        margin-right: 6px;
        cursor: pointer;
        transition: background 0.2s, box-shadow 0.2s;
        box-shadow: 0 1px 3px rgba(0,0,0,0.07);
        display: flex;
        align-items: center;
        justify-content: center;
      }
      .icon-button:hover {
        background: #e1e5e9;
        box-shadow: 0 2px 8px rgba(0,0,0,0.10);
      }
      .image-container img, .head-opponent-container img {
        border-radius: 50%;
      }
      .opponent-name {
        font-size: 16px;
        font-weight: 600;
        color: var(--text-primary-color);
        margin-bottom: 2px;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        max-width: 140px;
      }
      .last-seen {
        font-size: 12px;
        color: #888;
        font-weight: 400;
      }
   
      .product-price-container > div {
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        max-width: 160px;
      }
      @media (max-width: 600px) {
        .head-product-details-container, .actions-container {
          width: 100%;
          flex-direction: row;
          align-items: flex-start;
        }
          .head-product-container { 
            flex-direction: column;
          }
        .actions-container {
          margin-top: 10px;
        }
        .text-container, .product-price-container > div {
          max-width: 100%;
        }
      }
      .popup-overlay {
        position: fixed;
        top: 0; left: 0; right: 0; bottom: 0;
        width: 100vw; height: 100vh;
        background: var(--popup-overlay-bg);
        z-index: 9999;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: background 0.2s;
      }
      .popup-content {
        background: var(--popup-bg);
        color: var(--popup-text);
        border-radius: 18px;
        min-width: 340px;
        max-width: 95vw;
        padding: 32px 28px 24px 28px;
        box-shadow: 0 8px 32px rgba(0,0,0,0.25);
        position: relative;
        display: flex;
        flex-direction: column;
        animation: popupIn 0.2s;
      }
      @keyframes popupIn {
        from { transform: scale(0.95); opacity: 0; }
        to { transform: scale(1); opacity: 1; }
      }
      .popup-header {
        display: flex;
        align-items: center;
        justify-content: center;
        position: relative;
        margin-bottom: 12px;
      }
      .popup-header span {
        font-size: 22px;
        font-weight: 700;
        flex: 1;
        text-align: center;
        color: var(--popup-text);
      }
      .close-btn {
        position: absolute;
        right: 0;
        top: 0;
        background: none;
        border: none;
        color: var(--popup-text);
        font-size: 28px;
        cursor: pointer;
        padding: 0 4px;
        line-height: 1;
        border-radius: 50%;
        transition: background 0.15s;
      }
      .close-btn:hover {
        background: rgba(0,0,0,0.08);
      }
      .popup-body {
        display: flex;
        flex-direction: column;
        gap: 18px;
      }
      .popup-body label.subtitle {
        font-size: 15px;
        color: #bdbdbd;
        margin-bottom: 8px;
        font-weight: 500;
        text-align: left;
        display: block;
      }
      :host([theme='light']) .popup-body label.subtitle,
      @media (prefers-color-scheme: light) {
        .popup-body label.subtitle {
          color: #666;
        }
      }
      .popup-options {
        display: flex;
        flex-direction: column;
        gap: 12px;
        margin-bottom: 8px;
      }
      .popup-option {
        background: var(--popup-option-bg);
        color: var(--popup-option-text);
        border-radius: 10px;
        padding: 14px 16px;
        font-size: 16px;
        font-weight: 500;
        cursor: pointer;
        border: 2px solid transparent;
        outline: none;
        transition: background 0.15s, border 0.15s, color 0.15s;
        user-select: none;
        text-align: left;
      }
      .popup-option.selected {
        background: var(--popup-option-bg-selected);
        color: var(--popup-option-text-selected);
        border: 2px solid var(--popup-option-bg-selected);
      }
      .popup-option:focus {
        border: 2px solid var(--popup-btn-bg);
      }
      .popup-option:last-child {
        margin-bottom: 0;
      }
      .popup-other-textarea {
        margin-top: 8px;
        width: 100%;
        border-radius: 8px;
        border: 1px solid #ccc;
        background: var(--popup-option-bg);
        color: var(--popup-option-text);
        font-size: 15px;
        padding: 10px;
        resize: vertical;
        min-height: 60px;
        outline: none;
        transition: border 0.15s;
      }
      .popup-other-textarea:focus {
        border: 1.5px solid var(--popup-btn-bg);
      }
      .popup-body-buttons {
        display: flex;
        flex-direction: column;
        gap: 0;
        margin-top: 18px;
      }
      .popup-apply-btn {
        background: var(--popup-btn-bg);
        color: var(--popup-btn-text);
        border: none;
        border-radius: var(--popup-btn-radius);
        font-size: var(--popup-btn-font);
        font-weight: 600;
        padding: 14px 0;
        width: 100%;
        margin-top: 8px;
        cursor: pointer;
        transition: background 0.15s, color 0.15s;
        box-shadow: 0 2px 8px rgba(109,62,193,0.08);
      }
      .popup-apply-btn:disabled {
        background: #a78bfa;
        color: #e5e5e5;
        cursor: not-allowed;
      }
      .popup-error {
        color: var(--popup-error);
        margin-top: 8px;
        font-size: 14px;
        text-align: left;
      }

      .qr-code-loader-overlay {
         position: fixed;
         top: 0;
         left: 0;
         width: 100%;
         height: 100%;
         background: rgba(0, 0, 0, 0.6);
         display: flex;
         justify-content: center;
         align-items: center;
         z-index: 10000;
         backdrop-filter: blur(4px);
      }

      .qr-code-loader-content {
        background: var(--popup-bg);
        color: var(--popup-text);
        border-radius: 16px;
        padding: 32px;
        text-align: center;
        box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
        max-width: 400px;
        width: 90%;
        position: relative;
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 20px;
      }

      .qr-code-loader-text {
        font-size: 18px;
        font-weight: 600;
        color: var(--popup-text);
        margin: 0;
      }

      .qr-code-loader-spinner {
        width: 60px;
        height: 60px;
        border: 4px solid var(--popup-option-bg);
        border-top: 4px solid var(--popup-btn-bg);
        border-radius: 50%;
        animation: qr-spin 1s linear infinite;
      }

      @keyframes qr-spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }

      /* Exchange Layout Styles */
      .exchange-layout {
        display: flex;
        align-items: center;
        width: 100%;
        gap: 16px;
        padding: 0;
      }

      .exchange-product {
        display: flex;
        align-items: center;
        gap: 12px;
        flex: 1;
        min-width: 0;
      }

      .exchange-product .image-container {
        flex-shrink: 0;
      }

      .exchange-product .text-container {
        flex: 1;
        min-width: 0;
        overflow: hidden;
      }

      .exchange-product .text-container > * {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      .exchange-icon {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 40px;
        height: 40px;
        color: var(--text-secondary);
        flex-shrink: 0;
      }

      .exchange-icon svg {
        width: 24px;
        height: 24px;
      }

      .original-product {
        justify-content: flex-start;
      }

      .exchange-asset {
        justify-content: flex-end;
      }

      .exchange-asset .text-container {
        text-align: right;
      }

      .exchange-asset .product-price-container {
        align-items: flex-end;
      }
    `
  ];

  updated(changedProperties) {
    super.updated(changedProperties);
    if (changedProperties.has('textColor')) {
      this.style.setProperty('--text-color', this.textColor);
    }
    
    // Only recalculate states when conversationDetails changes, not on every update
    if (changedProperties.has('conversationDetails')) {
      // Get old and new conversation details
      const oldDetails = changedProperties.get('conversationDetails');
      const newDetails = this.conversationDetails;
      
      // Check if conversation ID changed OR if status/metaData changed (for real-time updates)
      const conversationIdChanged = newDetails?.conversationId !== oldDetails?.conversationId;
      const statusChanged = newDetails?.metaData?.status !== oldDetails?.metaData?.status;
      const offerIdChanged = newDetails?.metaData?.offerId !== oldDetails?.metaData?.offerId;
      const orderIdChanged = newDetails?.metaData?.orderId !== oldDetails?.metaData?.orderId;
      
 
      
      if (conversationIdChanged || statusChanged || offerIdChanged || orderIdChanged) {
        // Clear and recalculate states when conversation changes or status updates
        this.isBuyer = false;
        this.isOffer = false;
        this.isBuyNow = false;
        
        // Clear loading states when conversation changes
        this.isAcceptOfferLoading = false;
        this.isEditOfferLoading = false;
        this.isCounterOfferLoading = false;
        this.isCancelOfferLoading = false;
        
        this.calculateIsBuyer();
        this.calculatePurchaseType();
        
   
        
        // Force re-render of action buttons
        this.renderActionButtons();
        this.requestUpdate();
      }
    }
  }

  debounce(func, time) {
    clearTimeout(this.debounceTimer);
    this.debounceTimer = setTimeout(func, time);
  }

  handleInputChange(e) {
    const searchText = e.target.value.toLowerCase();
    this.searchValue = searchText;
    this.debouncedSearchHandler(searchText);
    this.requestUpdate();
  }

  handleFirstSvgClick(event) {
    event.stopPropagation();
    this.isPopupOpen = !this.isPopupOpen;
  }

  handleClearChat() {
    if (this.onClearChat) {
      this.onClearChat();
      this.isPopupOpen = false;
    }
  }

  handleSearchClick() {
    this.isPopupOpen = false;
    this.dispatchEvent(
      new CustomEvent('open-right-Sidebar', {
        detail: {
          type: 'open-right-Sidebar',
          renderComponent: 'message-search-container',
        },
        bubbles: true,
        composed: true,
      }),
    );
  }

  handleTextClick(event) {
    event.stopPropagation();

    if (this.onTextClick) {
      this.onTextClick();
    }
    this.dispatchEvent(
      new CustomEvent('open-right-Sidebar', {
        detail: {
          type: 'open-right-Sidebar',
          renderComponent: 'contact-info',
        },
        bubbles: true,
        composed: true,
      }),
    );

    // Update state if needed
    this.isConsentPopup = false;
    this.requestUpdate();
  }

  handleCloseChat() {
    this.isPopupOpen = false;
    if (this.onCloseChat) {
      this.onCloseChat();
    }
  }
  async handleCancelOffer() {
    // Get orderId from conversation details
    const offerId = this.conversationDetails?.metaData?.offerId;
    if (!offerId) {
      console.error('Offer ID not found in conversation details');
      return;
    }

    // Set loading state
    this.isCancelOfferLoading = true;
    this.requestUpdate();

    // Determine if this is an exchange offer and get exchangeAssetId
    const isExchangeOffer = this.conversationDetails?.metaData?.status === 'EXCHANGE_OFFER_SENT' || this.conversationDetails?.metaData?.status === 'ACCEPT_EXCHANGE_OFFER' || false;
    const exchangeAssetId = this.conversationDetails?.metaData?.exchangeAssetId || null;

    try {
      // Fetch reasons first before opening dialog
      const reasonsResp = await fetchReasons('6', this.userToken || this.userMetaData?.token);
      const reasons = Array.isArray(reasonsResp?.data) ? reasonsResp.data : [];
      
      // Clear loading state
      this.isCancelOfferLoading = false;
      this.requestUpdate();

      // Open dialog with reasons loaded
      const onCancel = () => {
        drawerDialogService.closeDialog('cancel-offer-dialog');
      };

      // Define onConfirm callback to handle successful cancellation
      const onConfirm = async (reason) => {
        
        // Close the dialog immediately
        drawerDialogService.closeDialog('cancel-offer-dialog');
        
        // Show full-screen loader
        this.showFullScreenLoader = true;
        this.requestUpdate();
        
        try {
          // Make the API call
          await cancelOffer(
            offerId,
            reason,
            this.userToken || this.userMetaData?.token
          );
          
          // Success case - hide loader and dispatch event
          this.showFullScreenLoader = false;
          this.requestUpdate();
          
          // Dispatch event to notify parent component
          this.dispatchEvent(
            new CustomEvent('offer-cancelled', {
              detail: { conversationId: this.conversationDetails?.conversationId },
              bubbles: true,
              composed: true,
            })
          );
          
          // Show success toast
          showToast({
            message: 'Offer cancelled successfully!',
            type: 'success',
            duration: 3000
          });
          
        } catch (err) {
          console.error('Error cancelling offer:', err);
          
          // Hide loader on error
          this.showFullScreenLoader = false;
          this.requestUpdate();
          
          // Show error toast
          showToast({
            message: 'Failed to cancel offer. Please try again.',
            type: 'error',
            duration: 5000
          });
        }
      };

  
      
      drawerDialogService.openDialog('cancel-offer-dialog', {
        reasons,
        loading: false,
        error: reasons.length === 0 ? 'No reasons found.' : '',
        conversationId: this.conversationDetails?.conversationId,
        offerId: offerId,
        token: this.userToken || this.userMetaData?.token,
        exchangeAssetId: exchangeAssetId,
        onCancel,
        onConfirm,
      });
    } catch (err) {
      console.error('Error fetching reasons:', err);
      
      // Clear loading state on error
      this.isCancelOfferLoading = false;
      this.requestUpdate();
      
      // Show error toast instead of opening dialog
      showToast({
        message: 'Failed to fetch cancellation reasons. Please try again.',
        type: 'error',
        position: 'bottom-right',
        duration: 5000
      });
    }
  }

  async handleCancelDeal() {
    
    // Prevent multiple calls
    if (this.isCancellingDeal) {
      return;
    }
    
    this.isCancellingDeal = true;
    
    // Get orderId from conversation details
    const orderId = this.conversationDetails?.metaData?.orderId;
    if (!orderId) {
      console.error('Order ID not found in conversation details');
      this.isCancellingDeal = false;
      return;
    }

    // Open dialog with loading state
    const onCancel = () => drawerDialogService.closeDialog('cancel-deal-dialog');
    
    // Define onConfirm callback to handle successful cancellation
    const onConfirm = async (reason) => {
      
      // Close the dialog immediately
      drawerDialogService.closeDialog('cancel-deal-dialog');
      
      // Show full-screen loader
      this.showFullScreenLoader = true;
      this.requestUpdate();
      
      try {
        // Make the API call
        await cancelDealOrder(
          { orderId: this.conversationDetails?.metaData?.orderId, reason: reason },
          this.userToken || this.userMetaData?.token
        );
        
        // Success case - hide loader and dispatch event
        this.showFullScreenLoader = false;
        this.requestUpdate();
        
        // Dispatch event to notify parent component
        this.dispatchEvent(
          new CustomEvent('deal-cancelled', {
            detail: { conversationId: this.conversationDetails?.conversationId },
            bubbles: true,
            composed: true,
          })
        );
        
        // Show success toast
        showToast({
          message: 'Deal cancelled successfully!',
          type: 'success',
          duration: 3000
        });
        
      } catch (err) {
        console.error('Error cancelling deal:', err);
        
        // Hide loader on error
        this.showFullScreenLoader = false;
        this.requestUpdate();
        
        // Show error toast
        showToast({
          message: 'Failed to cancel deal. Please try again.',
          type: 'error',
          duration: 5000
        });
      }
    };

    drawerDialogService.openDialog('cancel-deal-dialog', {
      reasons: [],
      loading: true,
      error: '',
      conversationId: this.conversationDetails?.conversationId,
      orderId: this.conversationDetails?.metaData?.orderId,
      token: this.userToken || this.userMetaData?.token,
      onCancel,
      onConfirm,
    });

    try {
      const reasonsResp = await fetchReasons('6', this.userToken || this.userMetaData?.token);
      const reasons = Array.isArray(reasonsResp?.data) ? reasonsResp.data : [];
      
      // Update the existing dialog with reasons instead of closing and reopening
      drawerDialogService.updateDialog('cancel-deal-dialog', {
        reasons,
        loading: false,
        error: reasons.length === 0 ? 'No reasons found.' : '',
        conversationId: this.conversationDetails?.conversationId,
        orderId: orderId,
        token: this.userToken || this.userMetaData?.token,
        onCancel,
        onConfirm,
      });
      // Reset the flag after dialog is successfully opened
      this.isCancellingDeal = false;
    } catch (err) {
      console.error('Error fetching reasons:', err);
      // Update the existing dialog with error instead of closing and reopening
      drawerDialogService.updateDialog('cancel-deal-dialog', {
        reasons: [],
        loading: false,
        error: 'Failed to fetch reasons.',
        conversationId: this.conversationDetails?.conversationId,
        orderId: orderId,
        token: this.userToken || this.userMetaData?.token,
        onCancel,
        onConfirm,
      });
      // Reset the flag on error
      this.isCancellingDeal = false;
    }
  }

  handleReasonChange(e) {
    const value = e.target.value;
    this.selectedReasonId = value;
    this.isOtherSelected = value === 'other';
    if (!this.isOtherSelected) {
      this.otherReasonText = '';
    }
    this.requestUpdate();
  }

  handleOtherReasonInput(e) {
    this.otherReasonText = e.target.value;
    this.requestUpdate();
  }

  closeReasonsPopup() {
    this.isReasonsPopupOpen = false;
    this.selectedReasonId = '';
    this.otherReasonText = '';
    this.isOtherSelected = false;
    this.reasonsError = '';
    this.requestUpdate();
  }


  async handlePencilClick() {
    const onConfirm = async (qrCode) => {
      drawerDialogService.closeDialog('qr-code-dialog');
      
      try {
        // Get orderId from conversation details
        const orderId = this.conversationDetails?.metaData?.orderId;
        
        if (!orderId) {
          throw new Error('Order ID not found in conversation details');
        }

        // Call validateQr function (token is handled internally in head-apis.js)
        const validationResult = await validateQr(qrCode, orderId);
        
        // Show success toast
        showToast({
          message: 'QR Code validated successfully!',
          type: 'success',
          position: 'bottom-right',
          duration: 3000
        });
        
      } catch (error) {
        console.error('Error validating QR code:', error);
        
        // Show error toast
        showToast({
          message: 'Failed to validate QR code. Please try again.',
          type: 'error',
          position: 'bottom-right',
          duration: 5000
        });
      }
      
      // Dispatch event to notify parent component
      this.dispatchEvent(
        new CustomEvent('qr-code-entered', {
          detail: { 
            conversationId: this.conversationDetails?.conversationId,
            qrCode: qrCode 
          },
          bubbles: true,
          composed: true,
        })
      );
    };
    
    const onCancel = () => {
      drawerDialogService.closeDialog('qr-code-dialog');
    };

    drawerDialogService.openDialog('qr-code-dialog', {
      onConfirm,
      onCancel,
    });
  }

  async handleQrCodeClick() {
    try {
      // Show loading state
      this.isQrCodeLoading = true;
      this.requestUpdate();

      // Get orderId from conversation details
      const orderId = this.conversationDetails?.metaData?.orderId;
      
      if (!orderId) {
        throw new Error('Order ID not found in conversation details');
      }

      // Call generate QR code API with orderId
      const qrCodeResponse = await generateQrCode(
        { orderId: orderId },
        this.userToken || this.userMetaData?.token
      );
      
      
      // Check if the response has the qrCode value
      if (!qrCodeResponse.data?.qrCode) {
        throw new Error('QR Code not found in API response');
      }

      // Generate QR code using the qrcode package with the qrCode value
      const QRCode = await import('qrcode');
      const qrCodeDataUrl = await QRCode.toDataURL(qrCodeResponse.data.qrCode, {
        width: 256,
        margin: 2,
        color: {
          dark: '#000000',
          light: '#FFFFFF'
        }
      });
      
      
      // Show QR code popup
      this.showQrCodePopup(qrCodeResponse.data.qrCode, qrCodeDataUrl, qrCodeResponse.data);
      
    } catch (error) {
      console.error('Error generating QR code:', error);
      
      // Show error toast
      showToast({
        message:  'Failed to generate QR code. Please try again.',
        type: 'error',
        position: 'bottom-right',
        duration: 5000
      });
    } finally {
      // Hide loading state
      this.isQrCodeLoading = false;
      this.requestUpdate();
    }
  }

  showQrCodePopup(qrCodeValue, qrCodeDataUrl, qrCodeData) {
    // Create popup overlay
    const overlay = document.createElement('div');
    overlay.className = 'qr-code-popup-overlay';
    overlay.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(0, 0, 0, 0.6);
      display: flex;
      justify-content: center;
      align-items: center;
      z-index: 10000;
      backdrop-filter: blur(4px);
    `;

    // Create popup content
    const popup = document.createElement('div');
    popup.className = 'qr-code-popup';
    popup.style.cssText = `
      background: white;
      border-radius: 16px;
      padding: 32px;
      text-align: center;
      box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
      max-width: 400px;
      width: 90%;
      position: relative;
    `;

    // Create close button
    const closeButton = document.createElement('button');
    closeButton.innerHTML = '×';
    closeButton.style.cssText = `
      position: absolute;
      top: 16px;
      right: 16px;
      background: none;
      border: none;
      font-size: 24px;
      cursor: pointer;
      color: #666;
      width: 32px;
      height: 32px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: background 0.2s;
    `;
    closeButton.onmouseover = () => closeButton.style.background = '#f0f0f0';
    closeButton.onmouseout = () => closeButton.style.background = 'transparent';
    closeButton.onclick = () => document.body.removeChild(overlay);

    // Create title
    const title = document.createElement('h3');
    title.textContent = 'QR Code';
    title.style.cssText = `
      margin: 0 0 24px 0;
      font-size: 20px;
      font-weight: 600;
      color: #333;
    `;

    // Create QR code image
    const qrImage = document.createElement('img');
    qrImage.src = qrCodeDataUrl;
    qrImage.style.cssText = `
      width: 200px;
      height: 200px;
      margin: 0 auto 16px;
      display: block;
      border-radius: 8px;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    `;

    // Create QR code value display with copy button
    const qrValueContainer = document.createElement('div');
    qrValueContainer.style.cssText = `
      display: flex;
      align-items: center;
      justify-content: space-between;
      background: #f5f5f5;
      padding: 12px;
      border-radius: 8px;
      margin: 16px 0;
    `;

    const qrValue = document.createElement('div');
    qrValue.textContent = `Code: ${qrCodeValue}`;
    qrValue.style.cssText = `
      font-family: monospace;
      font-size: 16px;
      color: #666;
      word-break: break-all;
      flex: 1;
    `;

    // Create small copy button next to the code
    const smallCopyButton = document.createElement('button');
    smallCopyButton.innerHTML = `
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M16 4H4C3.46957 4 2.96086 4.21071 2.58579 4.58579C2.21071 4.96086 2 5.46957 2 6V20C2 20.5304 2.21071 21.0391 2.58579 21.4142C2.96086 21.7893 3.46957 22 4 22H16C16.5304 22 17.0391 21.7893 17.4142 21.4142C17.7893 21.0391 18 20.5304 18 20V6C18 5.46957 17.7893 4.96086 17.4142 4.58579C17.0391 4.21071 16.5304 4 16 4Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        <path d="M22 2H14C13.4696 2 12.9609 2.21071 12.5858 2.58579C12.2107 2.96086 12 3.46957 12 4V6H14V4H22V12H20V14H22C22.5304 14 23.0391 13.7893 23.4142 13.4142C23.7893 13.0391 24 12.5304 24 12V4C24 3.46957 23.7893 2.96086 23.4142 2.58579C23.0391 2.21071 22.5304 2 22 2Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
    `;
    smallCopyButton.style.cssText = `
      background: #241172;
      color: white;
      border: none;
      border-radius: 4px;
      width: 32px;
      height: 32px;
      cursor: pointer;
      margin-left: 8px;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: background 0.2s;
      flex-shrink: 0;
    `;
    smallCopyButton.onmouseover = () => smallCopyButton.style.background = '#5a2fa0';
    smallCopyButton.onmouseout = () => smallCopyButton.style.background = '#241172';
    smallCopyButton.onclick = () => {
      navigator.clipboard.writeText(qrCodeValue).then(() => {
        // Show feedback
        const originalHTML = smallCopyButton.innerHTML;
        smallCopyButton.innerHTML = `
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M20 6L9 17L4 12" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        `;
        smallCopyButton.style.background = '#28a745';
        setTimeout(() => {
          smallCopyButton.innerHTML = originalHTML;
          smallCopyButton.style.background = '#241172';
        }, 1000);
      });
    };

    qrValueContainer.appendChild(qrValue);
    qrValueContainer.appendChild(smallCopyButton);

    // Create order ID display
    const orderIdDisplay = document.createElement('div');
    orderIdDisplay.textContent = `Order ID: ${qrCodeData.orderId}`;
    orderIdDisplay.style.cssText = `
      font-size: 14px;
      color: #888;
      margin-top: 8px;
    `;

    // Create button container for better layout
    const buttonContainer = document.createElement('div');
    buttonContainer.style.cssText = `
      display: flex;
      gap: 12px;
      margin-top: 16px;
    `;

    // Add copy button
    const copyButton = document.createElement('button');
    copyButton.textContent = 'Copy Code';
    copyButton.style.cssText = `
      background: #241172;
      color: white;
      border: none;
      padding: 12px 24px;
      border-radius: 8px;
      font-size: 14px;
      font-weight: 600;
      cursor: pointer;
      flex: 1;
      transition: background 0.2s;
    `;
    copyButton.onmouseover = () => copyButton.style.background = '#5a2fa0';
    copyButton.onmouseout = () => copyButton.style.background = '#241172';
    copyButton.onclick = () => {
      navigator.clipboard.writeText(qrCodeValue).then(() => {
        copyButton.textContent = 'Copied!';
        setTimeout(() => {
          copyButton.textContent = 'Copy Code';
        }, 2000);
      });
    };

   

    // Assemble popup
    popup.appendChild(closeButton);
    popup.appendChild(title);
    popup.appendChild(qrImage);
    popup.appendChild(qrValueContainer);
    popup.appendChild(orderIdDisplay);
    buttonContainer.appendChild(copyButton);
    popup.appendChild(buttonContainer);
    overlay.appendChild(popup);

    // Add to document
    document.body.appendChild(overlay);

    // Close on overlay click
    overlay.onclick = (e) => {
      if (e.target === overlay) {
        document.body.removeChild(overlay);
      }
    };

    // Close on escape key
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        document.body.removeChild(overlay);
        document.removeEventListener('keydown', handleEscape);
      }
    };
    document.addEventListener('keydown', handleEscape);
  }

 

 

  handleRateClick() {
    // Handle rate button click
    const isSeller = !this.isBuyer;
    const rateType = isSeller ? 'buyer' : 'dealer';
    
    // Get orderId from conversation details
    const orderId = this.conversationDetails?.metaData?.orderId;
    
    if (!orderId) {
      console.error('No orderId found in conversation details');
      return;
    }
    
    // Open rating dialog
    const onConfirm = async (payload) => {
      
      // Close the dialog immediately
      drawerDialogService.closeDialog('rating-dialog');
      
      // Show full-screen loader
      this.showFullScreenLoader = true;
      this.requestUpdate();
      
      try {
        // Make the API call
        await submitRating(payload, this.userToken || this.userMetaData?.token);
        
        // Success case - hide loader and dispatch event
        this.showFullScreenLoader = false;
        this.requestUpdate();
        
        // Show success toast
        showToast({
          message: 'Rating submitted successfully!',
          type: 'success',
          duration: 3000
        });
        
        // Dispatch event to notify parent component about rating action
        this.dispatchEvent(
          new CustomEvent('rate-action', {
            detail: {
              conversationId: this.conversationDetails?.conversationId,
              isSeller: isSeller,
              rateType: rateType,
              ratings: payload
            },
            bubbles: true,
            composed: true,
          })
        );
        
      } catch (error) {
        console.error('Error submitting rating:', error);
        
        // Hide loader on error
        this.showFullScreenLoader = false;
        this.requestUpdate();
        
        // Extract error message from API response
        let errorMessage = 'Failed to submit rating. Please try again.';
        if (error.response && error.response.data && error.response.data.message) {
          errorMessage = error.response.data.message;
        } else if (error.message) {
          errorMessage = error.message;
        }
        
        // Show error toast
        showToast({
          message: errorMessage,
          type: 'error',
          duration: 5000
        });
      }
    };
    
    const onCancel = () => {
      drawerDialogService.closeDialog('rating-dialog');
    };

    drawerDialogService.openDialog('rating-dialog', {
      token: this.userToken || this.userMetaData?.token,
      conversationId: this.conversationDetails?.conversationId,
      orderId: orderId,
      rateType: rateType,
      onConfirm,
      onCancel,
    });
  }

  handleMakePayment() {
    // Handle make payment button click
    
    // Get orderId from conversation details
    const orderId = this.conversationDetails?.metaData?.orderId;
    
    if (!orderId) {
      console.error('Order ID not found in conversation details');
      handleApiError(new Error('Order ID not found. Please try again.'), 'Order ID not found. Please try again.');
      return;
    }
    
    // Dispatch event to notify parent component about payment action
    this.dispatchEvent(
      new CustomEvent('payment-action', {
        detail: {
          conversationId: this.conversationDetails?.conversationId,
          orderId: orderId,
          action: 'make-payment'
        },
        bubbles: true,
        composed: true,
      })
    );
  }

 

  handleEditOffer() {
    // Handle edit offer button click
    
    // Get product details from conversation
    const productImage = this.conversationDetails?.metaData?.productImage;
    const productTitle = this.text || 'Product';
    const productPrice = `${this.conversationDetails?.metaData?.currency} ${this.conversationDetails?.metaData?.productPrice}`;
    const currentOfferPrice = this.conversationDetails?.metaData?.lastOfferAmount 
      ? `${this.conversationDetails?.metaData?.lastOfferAmount?.currency} ${this.conversationDetails?.metaData?.lastOfferAmount?.amount}`
      : '';

    const onConfirm = async (newPrice) => {
      drawerDialogService.closeDialog('offer-dialog');
      
      // Set loading state
      this.isEditOfferLoading = true;
      await this.updateComplete;
      this.requestUpdate();
      
      try {
        // Extract amount and currency from newPrice
        const priceMatch = newPrice.match(/([A-Z]{3})\s*([\d,]+\.?\d*)/);
        if (!priceMatch) {
          handleApiError(new Error('Invalid price format. Expected format: "CUR 123.45"'), 'Invalid price format. Please use the format: "CUR 123.45"');
          return;
        }
        
        const currency = priceMatch[1];
        const amount = priceMatch[2].replace(/,/g, ''); // Remove commas from amount
        
        // Get offerId from conversation details
        const offerId = this.conversationDetails?.metaData?.offerId;
        if (!offerId) {
          handleApiError(new Error('Offer ID not found in conversation details'), 'Offer ID not found. Please try again.');
          return;
        }
        
        // Call edit offer API
        const response = await editOffer(offerId, amount, currency, this.userToken);
        
        // Show success message
        handleApiSuccess('Offer updated successfully');
        
        // Dispatch event to notify parent component about edit offer action
        this.dispatchEvent(
          new CustomEvent('offer-action', {
            detail: {
              action: 'edit-offer',
              conversationId: this.conversationDetails?.conversationId,
              orderId: this.conversationDetails?.metaData?.orderId,
              newPrice: newPrice,
              apiResponse: response,
            },
            bubbles: true, 
            composed: true,
          })
        );
      } catch (error) {
        console.error('Error editing offer:', error);
        handleApiError(error, 'Failed to update offer. Please try again.');
      } finally {
        // Clear loading state
        this.isEditOfferLoading = false;
        this.requestUpdate();
      }
    };
    
    const onCancel = () => {
      drawerDialogService.closeDialog('offer-dialog');
    };

    drawerDialogService.openDialog('offer-dialog', {
      type: 'edit',
      productImage: productImage,
      productTitle: productTitle,
      productPrice: productPrice,
      currentOfferPrice: currentOfferPrice,
      onConfirm,
      onCancel,
    });
  }

  async handleAcceptOffer() {
    // Handle accept offer button click
    
    // Get offer details from conversation
    const offerId = this.conversationDetails?.metaData?.offerId;
    const lastOfferAmount = this.conversationDetails?.metaData?.lastOfferAmount;
    //if exchange offer, get exchangeAssetId
    const isExchangeOffer = this.conversationDetails?.metaData?.status === 'EXCHANGE_OFFER_SENT' || this.conversationDetails?.metaData?.status === 'ACCEPT_EXCHANGE_OFFER' || false;
    const exchangeAssetId = this.conversationDetails?.metaData?.exchangeAssetId || null;
    
    if (!offerId) {
      console.error('Offer ID not found in conversation details');
      handleApiError(new Error('Offer ID not found. Please try again.'), 'Offer ID not found. Please try again.');
      return;
    }
    
    if (!lastOfferAmount || !lastOfferAmount.amount || !lastOfferAmount.currency) {
      console.error('Last offer amount not found in conversation details');
      handleApiError(new Error('Offer amount not found. Please try again.'), 'Offer amount not found. Please try again.');
      return;
    }
    
    // Show confirmation dialog
    const onConfirm = async () => {
      drawerDialogService.closeDialog('confirm-dialog');
      
      // Set loading state
      this.isAcceptOfferLoading = true;
      this.requestUpdate();
      
      try {
        // Call accept offer API
        const response = await acceptOffer(
          offerId, 
          lastOfferAmount.amount, 
          lastOfferAmount.currency, 
          this.userToken || this.userMetaData?.token,
          isExchangeOffer,
          exchangeAssetId
        );
        
        // Show success message
        handleApiSuccess('Offer accepted successfully!');
        
        // Dispatch event to notify parent component about accept offer action
        this.dispatchEvent(
          new CustomEvent('offer-action', {
            detail: {
              action: 'accept-offer',
              conversationId: this.conversationDetails?.conversationId,
              orderId: this.conversationDetails?.metaData?.orderId,
              offerId: offerId,
              amount: lastOfferAmount.amount,
              currency: lastOfferAmount.currency,
              apiResponse: response,
            },
            bubbles: true,
            composed: true,
          })
        );
      } catch (error) {
        console.error('Error accepting offer:', error);
        
        // Handle error with appropriate UI feedback
        handleApiError(error, 'Failed to accept offer. Please try again.');
      } finally {
        // Clear loading state
        this.isAcceptOfferLoading = false;
        this.requestUpdate();
      }
    };
    
    const onCancel = () => {
      drawerDialogService.closeDialog('confirm-dialog');
    };

    drawerDialogService.openDialog('confirm-dialog', {
      title: 'Accept Offer',
      description: 'Are you sure you want to accept this offer?',
      handleConfirm: onConfirm,
      onCancel: onCancel,
    });
  }

  async handleCounterOffer() {
    // Handle counter offer button click
    
    // Get product details from conversation
    const productImage = this.conversationDetails?.metaData?.productImage;
    const productTitle = this.text || 'Product';
    const productPrice = `${this.conversationDetails?.metaData?.currency} ${this.conversationDetails?.metaData?.productPrice}`;
    const currentOfferPrice = this.conversationDetails?.metaData?.lastOfferAmount 
      ? `${this.conversationDetails?.metaData?.lastOfferAmount?.currency} ${this.conversationDetails?.metaData?.lastOfferAmount?.amount}`
      : '';

    const onConfirm = async (newPrice) => {
      drawerDialogService.closeDialog('offer-dialog');
      
      // Set loading state
      this.isCounterOfferLoading = true;
      await this.updateComplete;
      this.requestUpdate();
      
      try {
        // Extract amount and currency from newPrice
        const priceMatch = newPrice.match(/([A-Z]{3})\s*([\d,]+\.?\d*)/);
        if (!priceMatch) {
          handleApiError(new Error('Invalid price format. Expected format: "CUR 123.45"'), 'Invalid price format. Please use the format: "CUR 123.45"');
          return;
        }
        
        const currency = priceMatch[1];
        const amount = priceMatch[2].replace(/,/g, ''); // Remove commas from amount
        
        // Get offerId from conversation details
        const offerId = this.conversationDetails?.metaData?.offerId;
        if (!offerId) {
          handleApiError(new Error('Offer ID not found in conversation details'), 'Offer ID not found. Please try again.');
          return;
        }
        
        // Call counter offer API
        const response = await counterOffer(offerId, amount, currency, this.userToken || this.userMetaData?.token);
        
        // Show success message
        handleApiSuccess('Counter offer submitted successfully!');
        
        // Dispatch event to notify parent component about counter offer action
        this.dispatchEvent(
          new CustomEvent('offer-action', {
            detail: {
              action: 'counter-offer',
              conversationId: this.conversationDetails?.conversationId,
              orderId: this.conversationDetails?.metaData?.orderId,
              newPrice: newPrice,
              apiResponse: response,
            },
            bubbles: true,
            composed: true,
          })
        );
      } catch (error) {
        console.error('Error submitting counter offer:', error);
        
        // Handle error with appropriate UI feedback
        handleApiError(error, 'Failed to submit counter offer. Please try again.');
      } finally {
          // Clear loading state
        this.isCounterOfferLoading = false;
        this.requestUpdate();
      }
    };
    
    const onCancel = () => {
      drawerDialogService.closeDialog('offer-dialog');
    };

    drawerDialogService.openDialog('offer-dialog', {
      type: 'counter',
      productImage: productImage,
      productTitle: productTitle,
      productPrice: productPrice,
      currentOfferPrice: currentOfferPrice,
      onConfirm,
      onCancel,
    });
  }

  async handleRateUser() {
    // Handle rate user button click
    try {
      // Get conversation ID from URL
      const urlParams = new URLSearchParams(window.location.search);
      const conversationId = urlParams.get('conversation');
      const offerId = this.conversationDetails?.metaData?.orderId || this.conversationDetails?.metaData?.offerId;
      if (!conversationId || !offerId) {
        console.error('Missing conversationId or offerId for rating');
        return;
      }

      // Determine who to rate
      const userToRate = this.isBuyer ? 'seller' : 'buyer';
      
      // Open rating dialog
      drawerDialogService.openDialog('rating-dialog', {
        conversationId: conversationId,
        offerId: offerId,
        userToRate: userToRate,
        isBuyer: this.isBuyer,
        onRatingSubmitted: () => {
          // Refresh conversation details after rating submission
          this.dispatchEvent(
            new CustomEvent('conversation-updated', {
              detail: { conversationId: conversationId },
              bubbles: true,
              composed: true,
            }),
          );
        }
      });
      
    } catch (error) {
      console.error('Error opening rating dialog:', error);
      handleApiError(error, 'Failed to open rating dialog. Please try again.');
    }
  }

  handleCloseSearch() {
    this.isSearchOpen = false;
    this.requestUpdate();
  }



  handleOpenConsentPopup() {
    this.isConsentPopupOpen = true;
    this.requestUpdate();
  }

  handleCancelButton() {
    this.isConsentPopupOpen = false;
    this.requestUpdate();
  }

  renderActionButtons() {
 
    
    // Add a small delay to ensure state is properly set
    if (this.isEditOfferLoading || this.isCounterOfferLoading) {
      setTimeout(() => this.requestUpdate(), 0);
    }
    // Check deal status first
    const dealStatus = this.conversationDetails?.metaData?.status;
    

    if (dealStatus === 'REVIEW_RATING') {
      // Check if current user has already submitted their review
      const isReviewSubmittedByCurrentUser = this.isBuyer 
        ? this.conversationDetails?.metaData?.isReviewSubmittedByBuyer 
        : this.conversationDetails?.metaData?.isReviewSubmittedBySeller;
      
   
      
      if (isReviewSubmittedByCurrentUser) {
        // Current user has submitted their review
        return html`
          <div class="actions">
            <div class="review-submitted-tag">
              Review Submitted
            </div>
          </div>
        `;
      } else {
        // Current user hasn't submitted their review yet, show rating button
        return html`
          <div class="actions">
            <button class="rate-button" @click=${() => this.handleRateClick()}>
              Rate ${this.isBuyer ? 'Seller' : 'Buyer'}
            </button>
          </div>
        `;
      }
    }
    
    // If deal is cancelled, show cancelled tag
    if (dealStatus === 'CANCEL_DEAL') {
      return html`
        <div class="actions">
          <div class="deal-cancelled-tag">
            Deal Cancelled
          </div>
        </div>
      `;
    }

    if (dealStatus === 'ACCEPT_OFFER') {
      const isBuyerStatus = this.leofferAccountId === this.conversationDetails?.metaData?.buyerAccountId ? true : false;
      return html`
        <div class="actions" style="display: flex; gap: 8px; align-items: center;">
          ${isBuyerStatus 
            ? html`
                <button 
            @click=${() => this.onPaymentClicked({
              offerId: this.conversationDetails?.metaData?.offerId,
              orderId: this.conversationDetails?.metaData?.orderId,
              amount: this.conversationDetails?.metaData?.lastOfferAmount?.amount,
              currency: this.conversationDetails?.metaData?.lastOfferAmount?.currency,
              action: 'make-payment'
            })}
            class="action-button"
            style="background: #28a745;"
          >
            Make Payment
          </button>
          <button 
            @click=${() => this.handleCancelOffer()}
            class="action-button"
          >
            Cancel Deal
          </button>
        </div>`
      : html`
       <div class="actions" style="display: flex; gap: 8px; align-items: center;">
              <button 
                @click=${() => this.handleCancelOffer()}
                class="action-button"
                ?disabled=${this.isCancelOfferLoading}
              >
                ${this.isCancelOfferLoading ? 'Loading...' : 'Cancel Offer'}
              </button>
              <button 
                @click=${() => this.handleEditOffer()}
                class="action-button edit-offer-button"
                ?disabled=${this.isEditOfferLoading}
              >
                ${this.isEditOfferLoading ? 'Updating...' : 'Edit Offer'}
              </button>
            </div>`
          }
        </div>
      `;
    }


    if (dealStatus === 'CANCEL_OFFER') {
      return html`
        <div class="actions">
          <div class="deal-cancelled-tag">
            Deal Cancelled
          </div>
        </div>
      `;
    }
    
    // If deal is completed, show review status or rate button based on user role and review submission
    if (dealStatus === 'DEAL_COMPLETE') {
      const isSeller = this.leofferAccountId !== this.conversationDetails?.metaData?.buyerAccountId ? true : false; // If not buyer, then seller
      
      // Check review submission status based on user type
      const isReviewSubmitted = isSeller 
        ? this.conversationDetails?.metaData?.isReviewSubmittedBySeller 
        : this.conversationDetails?.metaData?.isReviewSubmittedByBuyer;
      
      // If review is already submitted, show "Review Submitted" tag
      if (isReviewSubmitted) {
        return html`
          <div class="actions">
            <div class="review-submitted-tag">
              Review Submitted
            </div>
          </div>
        `;
      }
      
      // If review is not submitted, show rate button
      const rateText = isSeller ? 'Rate Buyer' : 'Rate Seller';
      
      return html`
        <div class="actions">
          <button 
            @click=${() => this.handleRateClick()}
            class="rate-button"
          >
            ${rateText}
          </button>
        </div>
      `;
    }
    
    // If exchange offer is send, show action buttons
    if (dealStatus === 'EXCHANGE_OFFER_SENT') {
      const exhangeOfferInitiator = this.conversationDetails?.metaData?.offerSendByUserAccountId === this.leofferAccountId
      return html`
      ${!exhangeOfferInitiator ?
         html`
      <div class="actions">
        <button class="action-button exchange-offer-accept-button" @click=${() => this.handleAcceptOffer()}> Accept Offer</button>
        <button class="action-button" @click=${() => this.handleCancelOffer()}>  Cancel Deal</button>

      </div>`
      :
      html`
        <div class="actions">
          <button class="action-button" @click=${() => this.handleCancelOffer()}>Cancel Deal</button>
        </div>
      `}
      `
    }
    
    // If exhange offer is accepted, show action buttons 
    if(dealStatus === 'ACCEPT_EXCHANGE_OFFER'){
      const isBuyerStatus = this.leofferAccountId === this.conversationDetails?.metaData?.buyerAccountId ? true : false;
      return html`
        <div class="actions" style="display: flex; justify-content: space-between; align-items: center;">
          ${isBuyerStatus 
            ? html`
                <div class="buyer-actions">
                  <button 
                    @click=${() => this.handlePencilClick()}
                    class="icon-button"
                    title="Edit"
                  >
                    <svg width="20px" height="20px" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M11 4H4C3.46957 4 2.96086 4.21071 2.58579 4.58579C2.21071 4.96086 2 5.46957 2 6V20C2 20.5304 2.21071 21.0391 2.58579 21.4142C2.96086 21.7893 3.46957 22 4 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V13" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                      <path d="M18.5 2.50023C18.8978 2.1025 19.4374 1.87891 20 1.87891C20.5626 1.87891 21.1022 2.1025 21.5 2.50023C21.8978 2.898 22.1214 3.43762 22.1214 4.00023C22.1214 4.56284 21.8978 5.10246 21.5 5.50023L12 15.0002L8 16.0002L9 12.0002L18.5 2.50023Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                  </button>
                </div>
              ` 
            : html`
                <button 
                  @click=${() => this.handleQrCodeClick()}
                  class="icon-button"
                  title="QR Code"
                >
                  <svg width="20px" height="20px" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <g id="SVGRepo_bgCarrier" stroke-width="0"></g>
                    <g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g>
                    <g id="SVGRepo_iconCarrier"> 
                      <g id="System / Qr_Code"> 
                        <path id="Vector" d="M19 20H20M16 20H14V17M17 17H20V14H19M14 14H16M4 16.9997C4 16.0679 4 15.6019 4.15224 15.2344C4.35523 14.7443 4.74432 14.3552 5.23438 14.1522C5.60192 14 6.06786 14 6.99974 14C7.93163 14 8.39808 14 8.76562 14.1522C9.25568 14.3552 9.64467 14.7443 9.84766 15.2344C9.9999 15.6019 9.9999 16.0681 9.9999 17C9.9999 17.9319 9.9999 18.3978 9.84766 18.7654C9.64467 19.2554 9.25568 19.6447 8.76562 19.8477C8.39808 19.9999 7.93162 19.9999 6.99974 19.9999C6.06786 19.9999 5.60192 19.9999 5.23438 19.8477C4.74432 19.6447 4.35523 19.2557 4.15224 18.7656C4 18.3981 4 17.9316 4 16.9997ZM14 6.99974C14 6.06786 14 5.60192 14.1522 5.23438C14.3552 4.74432 14.7443 4.35523 15.2344 4.15224C15.6019 4 16.0679 4 16.9997 4C17.9316 4 18.3981 4 18.7656 4.15224C19.2557 4.35523 19.6447 4.74432 19.8477 5.23438C19.9999 5.60192 19.9999 6.06812 19.9999 7C19.9999 7.93188 19.9999 8.39783 19.8477 8.76537C19.6447 9.25542 19.2557 9.64467 18.7656 9.84766C18.3981 9.9999 17.9316 9.9999 16.9997 9.9999C16.0679 9.9999 15.6019 9.9999 15.2344 9.84766C14.7443 9.64467 14.3552 9.25568 14.1522 8.76562C14 8.39808 14 7.93163 14 6.99974ZM4 6.99974C4 6.06786 4 5.60192 4.15224 5.23438C4.35523 4.74432 4.74432 4.35523 5.23438 4.15224C5.60192 4 6.06786 4 6.99974 4C7.93163 4 8.39808 4 8.76562 4.15224C9.25568 4.35523 9.64467 4.74432 9.84766 5.23438C9.9999 5.60192 9.9999 6.06812 9.9999 7C9.9999 7.93188 9.9999 8.39783 9.84766 8.76537C9.64467 9.25542 9.25568 9.64467 8.76562 9.84766C8.39808 9.9999 7.93162 9.9999 6.99974 9.9999C6.06786 9.9999 5.60192 9.9999 5.23438 9.84766C4.74432 9.64467 4.35523 9.25568 4.15224 8.76562C4 8.39808 4 7.93163 4 6.99974Z" stroke="currentColor" stroke-width="1.128" stroke-linecap="round" stroke-linejoin="round"></path> 
                      </g> 
                    </g>
                  </svg>
                </button>
              `
          }

          <button 
            @click=${() => this.handleCancelOffer()}
            class="action-button"
          >
            Cancel Deal
          </button>
        </div>
      `
    }

    // Original logic for active deals
    if (this.isBuyNow && !this.isOffer) {
      return html`
        <div class="actions" style="display: flex; justify-content: space-between; align-items: center;">
         
          ${this.isBuyer 
            ? html`
                <div class="buyer-actions">
                  <button 
                    @click=${() => this.handlePencilClick()}
                    class="icon-button"
                    title="Edit"
                  >
                    <svg width="20px" height="20px" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M11 4H4C3.46957 4 2.96086 4.21071 2.58579 4.58579C2.21071 4.96086 2 5.46957 2 6V20C2 20.5304 2.21071 21.0391 2.58579 21.4142C2.96086 21.7893 3.46957 22 4 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V13" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                      <path d="M18.5 2.50023C18.8978 2.1025 19.4374 1.87891 20 1.87891C20.5626 1.87891 21.1022 2.1025 21.5 2.50023C21.8978 2.898 22.1214 3.43762 22.1214 4.00023C22.1214 4.56284 21.8978 5.10246 21.5 5.50023L12 15.0002L8 16.0002L9 12.0002L18.5 2.50023Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                </button>
                </div>
              ` 
            : html`
                <button 
                  @click=${() => this.handleQrCodeClick()}
                  class="icon-button"
                  title="QR Code"
                >
                  <svg width="20px" height="20px" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <g id="SVGRepo_bgCarrier" stroke-width="0"></g>
                    <g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g>
                    <g id="SVGRepo_iconCarrier"> 
                      <g id="System / Qr_Code"> 
                        <path id="Vector" d="M19 20H20M16 20H14V17M17 17H20V14H19M14 14H16M4 16.9997C4 16.0679 4 15.6019 4.15224 15.2344C4.35523 14.7443 4.74432 14.3552 5.23438 14.1522C5.60192 14 6.06786 14 6.99974 14C7.93163 14 8.39808 14 8.76562 14.1522C9.25568 14.3552 9.64467 14.7443 9.84766 15.2344C9.9999 15.6019 9.9999 16.0681 9.9999 17C9.9999 17.9319 9.9999 18.3978 9.84766 18.7654C9.64467 19.2554 9.25568 19.6447 8.76562 19.8477C8.39808 19.9999 7.93162 19.9999 6.99974 19.9999C6.06786 19.9999 5.60192 19.9999 5.23438 19.8477C4.74432 19.6447 4.35523 19.2557 4.15224 18.7656C4 18.3981 4 17.9316 4 16.9997ZM14 6.99974C14 6.06786 14 5.60192 14.1522 5.23438C14.3552 4.74432 14.7443 4.35523 15.2344 4.15224C15.6019 4 16.0679 4 16.9997 4C17.9316 4 18.3981 4 18.7656 4.15224C19.2557 4.35523 19.6447 4.74432 19.8477 5.23438C19.9999 5.60192 19.9999 6.06812 19.9999 7C19.9999 7.93188 19.9999 8.39783 19.8477 8.76537C19.6447 9.25542 19.2557 9.64467 18.7656 9.84766C18.3981 9.9999 17.9316 9.9999 16.9997 9.9999C16.0679 9.9999 15.6019 9.9999 15.2344 9.84766C14.7443 9.64467 14.3552 9.25568 14.1522 8.76562C14 8.39808 14 7.93163 14 6.99974ZM4 6.99974C4 6.06786 4 5.60192 4.15224 5.23438C4.35523 4.74432 4.74432 4.35523 5.23438 4.15224C5.60192 4 6.06786 4 6.99974 4C7.93163 4 8.39808 4 8.76562 4.15224C9.25568 4.35523 9.64467 4.74432 9.84766 5.23438C9.9999 5.60192 9.9999 6.06812 9.9999 7C9.9999 7.93188 9.9999 8.39783 9.84766 8.76537C9.64467 9.25542 9.25568 9.64467 8.76562 9.84766C8.39808 9.9999 7.93162 9.9999 6.99974 9.9999C6.06786 9.9999 5.60192 9.9999 5.23438 9.84766C4.74432 9.64467 4.35523 9.25568 4.15224 8.76562C4 8.39808 4 7.93163 4 6.99974Z" stroke="currentColor" stroke-width="1.128" stroke-linecap="round" stroke-linejoin="round"></path> 
                      </g> 
                    </g>
                  </svg>
                </button>
              `
          }

           <button 
            @click=${() => this.handleCancelDeal()}
            class="action-button"
          >
            Cancel Deal
          </button>
        </div>
      `;
    }
    
    // New logic for offer deals (isBuyNow false and isOffer true)
    if (!this.isBuyNow && this.isOffer) {

      const isBuyerStatus = this.leofferAccountId === this.conversationDetails?.metaData?.buyerAccountId ? true : false
      // Check if status is PAYMENT_ESCROWED - show QR code logic similar to BUY_DIRECT
      if (dealStatus === 'PAYMENT_ESCROWED') {
        return html`
          <div class="actions" style="display: flex; justify-content: space-between; align-items: center;">
            ${isBuyerStatus 
              ? html`
                  <div class="buyer-actions">
                    <button 
                      @click=${() => this.handlePencilClick()}
                      class="icon-button"
                      title="Edit"
                    >
                      <svg width="20px" height="20px" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M11 4H4C3.46957 4 2.96086 4.21071 2.58579 4.58579C2.21071 4.96086 2 5.46957 2 6V20C2 20.5304 2.21071 21.0391 2.58579 21.4142C2.96086 21.7893 3.46957 22 4 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V13" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                        <path d="M18.5 2.50023C18.8978 2.1025 19.4374 1.87891 20 1.87891C20.5626 1.87891 21.1022 2.1025 21.5 2.50023C21.8978 2.898 22.1214 3.43762 22.1214 4.00023C22.1214 4.56284 21.8978 5.10246 21.5 5.50023L12 15.0002L8 16.0002L9 12.0002L18.5 2.50023Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                      </svg>
                    </button>
                  </div>
                ` 
              : html`
                  <button 
                    @click=${() => this.handleQrCodeClick()}
                    class="icon-button"
                    title="QR Code"
                  >
                    <svg width="20px" height="20px" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <g id="SVGRepo_bgCarrier" stroke-width="0"></g>
                      <g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g>
                      <g id="SVGRepo_iconCarrier"> 
                        <g id="System / Qr_Code"> 
                          <path id="Vector" d="M19 20H20M16 20H14V17M17 17H20V14H19M14 14H16M4 16.9997C4 16.0679 4 15.6019 4.15224 15.2344C4.35523 14.7443 4.74432 14.3552 5.23438 14.1522C5.60192 14 6.06786 14 6.99974 14C7.93163 14 8.39808 14 8.76562 14.1522C9.25568 14.3552 9.64467 14.7443 9.84766 15.2344C9.9999 15.6019 9.9999 16.0681 9.9999 17C9.9999 17.9319 9.9999 18.3978 9.84766 18.7654C9.64467 19.2554 9.25568 19.6447 8.76562 19.8477C8.39808 19.9999 7.93162 19.9999 6.99974 19.9999C6.06786 19.9999 5.60192 19.9999 5.23438 19.8477C4.74432 19.6447 4.35523 19.2557 4.15224 18.7656C4 18.3981 4 17.9316 4 16.9997ZM14 6.99974C14 6.06786 14 5.60192 14.1522 5.23438C14.3552 4.74432 14.7443 4.35523 15.2344 4.15224C15.6019 4 16.0679 4 16.9997 4C17.9316 4 18.3981 4 18.7656 4.15224C19.2557 4.35523 19.6447 4.74432 19.8477 5.23438C19.9999 5.60192 19.9999 6.06812 19.9999 7C19.9999 7.93188 19.9999 8.39783 19.8477 8.76537C19.6447 9.25542 19.2557 9.64467 18.7656 9.84766C18.3981 9.9999 17.9316 9.9999 16.9997 9.9999C16.0679 9.9999 15.6019 9.9999 15.2344 9.84766C14.7443 9.64467 14.3552 9.25568 14.1522 8.76562C14 8.39808 14 7.93163 14 6.99974ZM4 6.99974C4 6.06786 4 5.60192 4.15224 5.23438C4.35523 4.74432 4.74432 4.35523 5.23438 4.15224C5.60192 4 6.06786 4 6.99974 4C7.93163 4 8.39808 4 8.76562 4.15224C9.25568 4.35523 9.64467 4.74432 9.84766 5.23438C9.9999 5.60192 9.9999 6.06812 9.9999 7C9.9999 7.93188 9.9999 8.39783 9.84766 8.76537C9.64467 9.25542 9.25568 9.64467 8.76562 9.84766C8.39808 9.9999 7.93162 9.9999 6.99974 9.9999C6.06786 9.9999 5.60192 9.9999 5.23438 9.84766C4.74432 9.64467 4.35523 9.25568 4.15224 8.76562C4 8.39808 4 7.93163 4 6.99974Z" stroke="currentColor" stroke-width="1.128" stroke-linecap="round" stroke-linejoin="round"></path> 
                        </g> 
                      </g>
                    </svg>
                  </button>
                `
            }

            <button 
              @click=${() => this.handleCancelDeal()}
              class="action-button"
            >
              Cancel Deal
            </button>
          </div>
        `;
      }
      // Counter Offer Logic - Only run if status is COUNTER_OFFER
      if (dealStatus === 'COUNTER_OFFER' || dealStatus === 'EDIT_OFFER') {
        // Check if current user made the counter offer
        const isCurrentUserMadeCounterOffer = this.conversationDetails?.metaData?.offerSendByUserAccountId === this.leofferAccountId;
        
        if (isCurrentUserMadeCounterOffer) {
          // Current user made the counter offer → Show Edit and Cancel buttons
          return html`
            <div class="actions" style="display: flex; gap: 8px; align-items: center;">
              <button 
                @click=${() => this.handleCancelOffer()}
                class="action-button"
                ?disabled=${this.isCancelOfferLoading}
              >
                ${this.isCancelOfferLoading ? 'Loading...' : 'Cancel Offer'}
              </button>
              <button 
                @click=${() => this.handleEditOffer()}
                class="action-button edit-offer-button"
                ?disabled=${this.isEditOfferLoading}
              >
                ${this.isEditOfferLoading ? 'Updating...' : 'Edit Offer'}
              </button>
            </div>
          `;
        } else {
          // Opponent made the counter offer → Show Accept and Counter buttons
          return html`
            <div class="actions" style="display: flex; gap: 8px; align-items: center;">
              <button 
                @click=${() => this.handleAcceptOffer()}
                class="action-button"
                ?disabled=${this.isAcceptOfferLoading}
              >
                ${this.isAcceptOfferLoading ? 'Accepting...' : 'Accept Offer'}
              </button>
              <button 
                @click=${() => this.handleCounterOffer()}
                class="action-button counter-offer-button"
                ?disabled=${this.isCounterOfferLoading}
              >
                ${this.isCounterOfferLoading ? 'Submitting...' : 'Counter Offer'}
              </button>
            </div>
          `;
        }
      }
      

      
      // Default offer logic (for other statuses) - Use buyer/seller logic
      return html`
        <div class="actions" style="display: flex; gap: 8px; align-items: center;">
          ${isBuyerStatus 

            ? html`<button 
                  @click=${() => this.handleCancelOffer()}
                  class="action-button check check"
                  ?disabled=${this.isCancelOfferLoading}
                >
                  ${this.isCancelOfferLoading ? 'Loading...' : 'Cancel Offer'}
                </button>
                <button 
                  @click=${() => this.handleEditOffer()}
                  class="action-button edit-offer-button"
                  ?disabled=${this.isEditOfferLoading}
                >
                  ${this.isEditOfferLoading ? 'Updating...' : 'Edit Offer'}
                </button>` 
            : html`
                <button 
                  @click=${() => this.handleAcceptOffer()}
                  class="action-button check check"
                  ?disabled=${this.isAcceptOfferLoading}
                >
                  ${this.isAcceptOfferLoading ? 'Accepting...' : 'Accept Offer'}
                </button>
                <button 
                  @click=${() => this.handleCounterOffer()}
                  class="action-button counter-offer-button"
                  ?disabled=${this.isCounterOfferLoading}
                >
                  ${this.isCounterOfferLoading ? 'Submitting...' : 'Counter Offer'}
                </button>
              `
          }
        </div>
      `;
    }
    
    if (this.isActionsNeeded) {
      return html`
        <div class="actions">
          <svg
            class="kebab-menu"
            @click=${this.handleFirstSvgClick} 
            width="24px" 
            height="24px" 
            viewBox="0 0 24 24" 
            fill="${this.svgFillColor}"
          >
            <title>Kebab-Menu</title>
            <circle cx="12" cy="5" r="1.5"></circle>
            <circle cx="12" cy="12" r="1.5"></circle>
            <circle cx="12" cy="19" r="1.5"></circle>
          </svg>
        </div>
      `;
    }
    
    return '';
  }

  clearAndRecalculateStates() {
    // Clear all states first
    this.isBuyer = null;
    this.isOffer = false;
    this.isBuyNow = false;
    
    // Clear all loading states when conversation changes
    this.isAcceptOfferLoading = false;
    this.isEditOfferLoading = false;
    this.isCounterOfferLoading = false;
    this.isCancelOfferLoading = false;
    
    // Then recalculate based on current conversation
    this.calculateIsBuyer();
    this.calculatePurchaseType();
  }

  handleChatTileSelection(conversationDetails) {
    // Reset all states when a new chat tile is selected
    this.isBuyer = null;
    this.isOffer = false;
    this.isBuyNow = false;
    
    // Update conversation details
    this.conversationDetails = conversationDetails;
    
    // Recalculate states based on new conversation
    this.calculateIsBuyer();
    this.calculatePurchaseType();
    
  }

  calculateIsBuyer() {
    // Clear the value first
    this.isBuyer = false;
    
    // Then check the same condition
    if (this.leofferAccountId && this.conversationDetails?.metaData?.buyerAccountId) {
      this.isBuyer = this.leofferAccountId === this.conversationDetails.metaData.buyerAccountId ? true : false;
    }
  }

  calculatePurchaseType() {
    // Clear the values first
    this.isOffer = false;
    this.isBuyNow = false;
    
    // Check if purchaseType is OFFER
    if (this.conversationDetails?.metaData?.purchaseType === 'OFFER') {
      this.isOffer = true;
    }
    // Check if purchaseType is BUY_DIRECT
    else if (this.conversationDetails?.metaData?.purchaseType === 'BUY_DIRECT') {
      this.isBuyNow = true;
    }
  }

  openConfirmationPopup(action, title) {
    this.isPopupOpen = false;
    drawerDialogService.openDialog('confirm-dialog', {
      title: `${title}`,
      description1:
        action === 'clear-chat'
          ? ' Performing this action will cause the chat history to be deleted for you. You will be able to continue chatting with this user but will not be able to view the chat history once this action has been performed.'
          : null,
      description: this.renderdescription(action, title),
      handleConfirm: () => this.confirmAction(action),
    });
  }
  renderdescription(action, title) {
    switch (action) {
      case 'block-contact':
        return `Are you sure you want to ${title} ${this.conversationDetails?.opponentDetails.userName} ?`;
      case 'unblock-contact':
        return `Are you sure you want to ${title} ${this.conversationDetails?.opponentDetails.userName} ?`;
      default:
        return `Are you sure you want to ${title} ?`;
    }
  }
  closeConfirmationPopup() {
    this.showConfirmationPopup = false;
  }
  blockContact() {
    this.dispatchEvent(
      new CustomEvent('contact-action', {
        detail: {
          action: 'block-contact',
          oppenentId: this.conversationDetails?.opponentDetails?.userId,
          userName: this.conversationDetails?.opponentDetails.userName,
        },
        bubbles: true,
        composed: true,
      }),
    );
  }
  unblockContact() {
    this.dispatchEvent(
      new CustomEvent('contact-action', {
        detail: {
          action: 'unblock-contact',
          oppenentId: this.conversationDetails?.opponentDetails?.userId,
          userName: this.conversationDetails?.opponentDetails.userName,
        },
        bubbles: true,
        composed: true,
      }),
    );
  }
  confirmAction(action) {
    switch (action) {
      case 'block-contact':
        this.blockContact();
        break;
      case 'unblock-contact':
        this.unblockContact();
        break;
      case 'clear-chat':
        this.handleClearChat();
        break;
      case 'close-chat':
        this.handleCloseChat();
        break;
      default:
        null;
    }
  }
  render() {
    // Use the calculated values
    if (this.isBuyer === null) {
      this.calculateIsBuyer();
    }
    if (!this.isOffer && !this.isBuyNow) {
      this.calculatePurchaseType();
    }



    return html`
      <div
        class="head-container"
        style="border-radius: ${this.borderRadius};
        border-color: ${this.borderColor};
        border-width: ${this.borderWidth};
        border-style: ${this.borderStyle};
        padding: ${this.padding};
        box-shadow: ${this.boxShadow};
        background-color: ${this.backgroundColor};
        color: ${this.textColor}
      "
      >
        <div class="head-main-content">
          <div class="head-opponent-container">
            ${(() => {
              const opponent = this.conversationDetails?.members?.find(member => member.userId !== this.userMetaData?.userId);
              return opponent ? html`
                <div>
                  <div class="back-button" @click=${this.onBackButtonClick}>
                    <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="var(--text-primary-color)"><path d="m313-440 224 224-57 56-320-320 320-320 57 56-224 224h487v80H313Z"/></svg>
                  </div>
                  ${Image({
                    src: opponent.userProfileImageUrl,
                    height: '40px',
                    width: '40px',
                    placeholderImage: generateProfileImageByInitials(opponent.userName),
                    borderRadius: '8px',
                    objectFit: 'cover',
                    cursor: 'pointer',
                  })}
                  <span>
                    <div class="opponent-name">${opponent.userName}</div>
                    ${this.lastSeenText && !this.isTyping
                      ? html`<div class="last-seen">${this.lastSeenText}</div>`
                      : ''}
                  </span>
                </div>
              ` : '';
            })()}
          </div>
          
          <div class="head-product-container">
            ${this.conversationDetails?.metaData?.status === 'EXCHANGE_OFFER_SENT' || this.conversationDetails?.metaData?.status === 'ACCEPT_EXCHANGE_OFFER'
              ? html`
                <div class="head-product-details-container exchange-layout">
                  <!-- Original Product (Left) -->
                  <div class="exchange-product original-product">
                    <div class="image-container">
                      ${Image({
                        src: getImageSrc(getProductImage(this.conversationDetails?.metaData?.productImage)),
                        height: '55px',
                        width: '55px',
                        placeholderImage: generateProfileImageByInitials(this.text),
                        borderRadius: '8px',
                        objectFit: 'cover',
                        cursor: 'pointer',
                      })}
                    </div>
                    <div class="text-container">
                      ${Text({
                        fontSize: '14px',
                        fontWeight: '600',
                        content: this.text || '',
                        color: 'var(--text-primary)',
                        wordBreak: 'break-word',
                        maxWidth: '100%',
                        textAlign: 'left',
                        lineClamp: 2
                      })}
                        ${this.conversationDetails?.metaData?.productPrice && html`
                          <div class="product-price-container">
                        ${Text({
                          fontSize: '14px',
                          fontWeight: '400',
                          content: this.conversationDetails?.metaData?.currency + ' ' + this.conversationDetails?.metaData?.productPrice,
                          color: 'var(--text-primary)',
                        })}
                      </div>`}
                    
                    </div>
                  </div>

                  <!-- Exchange Icon (Center) -->
                  <div class="exchange-icon">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M7 4L3 8L7 12" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                      <path d="M17 20L21 16L17 12" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                      <path d="M3 8H21" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                      <path d="M21 16H3" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                  </div>

                  <!-- Exchange Asset (Right) -->
                  <div class="exchange-product exchange-asset">
                    <div class="image-container">
                      ${Image({
                        src: getImageSrc(this.conversationDetails?.metaData?.exchangeAssetImage?.[0]),
                        height: '55px',
                        width: '55px',
                        placeholderImage: generateProfileImageByInitials(this.conversationDetails?.metaData?.exchangeAssetTitle || ''),
                        borderRadius: '8px',
                        objectFit: 'cover',
                        cursor: 'pointer',
                      })}
                    </div>
                    <div class="text-container">
                      ${Text({
                        fontSize: '14px',
                        fontWeight: '600',
                        content: this.conversationDetails?.metaData?.exchangeAssetTitle || '',
                        color: 'var(--text-primary)',
                        wordBreak: 'break-word',
                        maxWidth: '100%',
                        textAlign: 'left',
                        lineClamp: 2
                      })}
                        ${this.conversationDetails?.metaData?.productPrice && html`
                           <div class="product-price-container">
                           ${Text({
                             fontSize: '14px',
                             fontWeight: '400',
                             content: this.conversationDetails?.metaData?.lastOfferAmount?.currency + ' ' + this.conversationDetails?.metaData?.lastOfferAmount?.amount,
                             color: 'var(--text-primary)',
                           })}
                         </div>
                        `}
                      
                    </div>
                  </div>
                </div>
              `
              : html`
                <div class="head-product-details-container">
                  <div class="image-container">
                    ${Image({
                      src: getImageSrc(getProductImage(this.conversationDetails?.metaData?.productImage)),
                      height: '55px',
                      width: '55px',
                      placeholderImage: generateProfileImageByInitials(this.text),
                      borderRadius: '8px',
                      objectFit: 'cover',
                      cursor: 'pointer',
                    })}
                  </div>

                  <div class="text-container" >
                    ${Text({
                      fontSize: '14px',
                      fontWeight: '600',
                      content: this.text || '',
                      color: 'var(--text-primary)',
                      wordBreak: 'break-word',
                      maxWidth: '100%',
                      textAlign: 'left'
                    })}
                  ${this.conversationDetails?.metaData?.productPrice && html`
                    <div class="product-price-container">
                      ${Text({
                        fontSize: '14px',
                        fontWeight: '400',
                        content: this.conversationDetails?.metaData?.currency + ' ' + this.conversationDetails?.metaData?.productPrice,
                        color: 'var(--text-primary)',
                      })}
                    `}

                      ${this.conversationDetails?.metaData?.purchaseType !== 'BUY_DIRECT' && this.conversationDetails?.metaData?.lastOfferAmount?.amount ? 
                        Text({
                          fontSize: '14px',
                          fontWeight: '400',
                          content: `Last offer: ${this.conversationDetails?.metaData?.lastOfferAmount?.currency + ' ' + this.conversationDetails?.metaData?.lastOfferAmount?.amount}`,
                          color: 'var(--text-secondary)',
                        }) : ''
                      }
                     
                    </div>
                  </div>
                </div>
              `
            }
            
            <div class="actions-container">
              ${this.renderActionButtons()}
            </div>
          </div>
        </div>
        
        ${this.isPopupOpen
          ? html`
              <div class="ctas-popup">
                <button @click=${() => this.handleSearchClick()}>
                  <svg width="16px" height="16px" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M16.6725 16.6412L21 21M19 11C19 15.4183 15.4183 19 11 19C6.58172 19 3 15.4183 3 11C3 6.58172 6.58172 3 11 3C15.4183 3 19 6.58172 19 11Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path>
                  </svg>
                  Search Chat
                </button>
                <button @click=${() => this.handleOpenConsentPopup()}>
                  <svg width="18px" height="18px" viewBox="-1.6 -1.6 19.20 19.20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M9.44444 4.44444L12.3917 0.760432C12.7762 0.279794 13.3583 0 13.9738 0C15.0929 0 16 0.907148 16 2.02617C16 2.64169 15.7202 3.22383 15.2396 3.60835L11.5556 6.55556L12.2454 7.24538C12.7286 7.72855 13 8.38388 13 9.0672C13 9.66992 12.7887 10.2536 12.4028 10.7166L11.8246 11.4104L4.58957 4.17536L5.2834 3.59717C5.74643 3.21131 6.33008 3 6.9328 3C7.61612 3 8.27145 3.27145 8.75462 3.75462L9.44444 4.44444Z" fill="currentColor"></path>
                    <path d="M0 8L3.04679 5.46101L10.539 12.9532L8 16L0 8Z" fill="currentColor"></path>
                  </svg>
                  Clear Chat
                </button>
                <button @click=${() => this.handleCloseChat()}>
                  <svg width="20px" height="20px" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M16 16L12 12M12 12L8 8M12 12L16 8M12 12L8 16" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path>
                  </svg>
                  Close Chat
                </button>
              </div>
            `
          : ''}
        
        ${this.isConsentPopupOpen
          ? html`
              <div class="consent-popup-container">
                <div class="consent-popup">
                  <h3>Clear this chat?</h3>
                  <p>
                    Performing this action will cause the chat history to be deleted for you. You will be able to continue chatting with this user but will not be able to view the
                    chat history once this action has been performed.
                  </p>
                  <p>Are you sure you want to Clear this chat?</p>
                  <div>
                    <button @click=${() => this.handleCancelButton()}>Cancel</button>
                    <button @click=${() => this.handleClearChat()}>Clear</button>
                  </div>
                </div>
              </div>
            `
          : ''}
        
        ${this.isSearchOpen
          ? html`
              <div class="searchMessageContainer ${this.isSearchOpen ? 'open' : ''}">
                <div class="search-header">
                  <h3>Search Messages</h3>
                  <button class="close-search" @click=${this.handleCloseSearch}>
                    <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="currentColor">
                      <path d="m256-200-56-56 224-224-224-224 56-56 224 224 224-224 56 56-224 224 224 224-56 56-224-224-224 224Z"/>
                    </svg>
                  </button>
                </div>
                <div class="search-input-container">
                  <my-input
                    backgroundColor="var(--bg-primary-color)"
                    borderWidth="1px"
                    borderColor="var(--text-primary-color)"
                    .minHeight="40px"
                    .maxHeight="40px"
                    .inputHeight="40px"
                    color="var(--text-primary-color)"
                    placeholderColor="var(--text-primary-color)"
                    placeholder="Search Chat Messages"
                    @input="${e => this.handleInputChange(e)}"
                  ></my-input>
                </div>
                <div class="search-results">
                  ${this.searchedMessageDetails.length === 0
                    ? html`<div class="search-results-placeholder">${this.searchText ? 'Messages not found' : 'Searched messages will appear here'}</div>`
                    : repeat(
                        this.searchedMessageDetails,
                        message => message.messageId,
                        message => {
                          return html`
                            <div class="search-results-container" @click="${() => this.handleSearchResultClick(message.messageId)}">
                              <span>${message.body}</span>
                              <span>${formatUnixTimestamp(message.sentAt)}</span>
                            </div>
                          `;
                        },
                      )}
                </div>
              </div>
            `
          : ''}
        
        ${this.isQrCodeLoading
          ? html`
              <div class="qr-code-loader-overlay">
                <div class="qr-code-loader-content">
                  <div class="qr-code-loader-spinner"></div>
                  <h3 class="qr-code-loader-text">Generating QR Code...</h3>
                </div>
              </div>
            `
          : ''}
        
        ${this.showFullScreenLoader
          ? html`
              <div class="full-screen-loader">
                <div class="loader-content">
                  <div class="loader-spinner"></div>
                  <div class="loader-text">Processing request...</div>
                </div>
              </div>
            `
          : ''}
        
      </div>
    `;
  }
}

customElements.define('my-head', Head);
